import { brl, quote } from '../../lib/pricing';
import type { Pricing } from '../../lib/types';

export interface PricingDraft {
  engineer: string;
  team: string;
}

// "12.500", "12500,50", "R$ 9.800,00" → cents; empty → null; anything else → NaN
export const parseReais = (raw: string): number | null => {
  const s = raw.replace(/R\$|\s/g, '');
  if (!s) return null;
  const normalized = s.includes(',') ? s.replace(/\./g, '').replace(',', '.') : /^\d{1,3}(\.\d{3})+$/.test(s) ? s.replace(/\./g, '') : s;
  const n = Number(normalized);
  return Number.isFinite(n) && n >= 0 ? Math.round(n * 100) : NaN;
};

export const draftOf = (p: Pricing | null): PricingDraft => ({
  engineer: p ? String(p.engineerMonthly / 100).replace('.', ',') : '',
  team: p ? String(p.teamMonthly / 100).replace('.', ',') : '',
});

// body for POST/PATCH: reais, or null when both are empty (the proposal shows effort only)
export const pricingBody = (d: PricingDraft): { engineerMonthly: number | null; teamMonthly: number | null } | { error: string } => {
  const e = parseReais(d.engineer);
  const t = parseReais(d.team);
  if (Number.isNaN(e) || Number.isNaN(t)) return { error: 'Confira os valores mensais: use só números, como 18.500 ou 18500,00.' };
  if (e === null && t === null) return { engineerMonthly: null, teamMonthly: null };
  return { engineerMonthly: (e ?? 0) / 100, teamMonthly: (t ?? 0) / 100 };
};

export const PricingFields = ({ value, onChange }: { value: PricingDraft; onChange: (d: PricingDraft) => void }) => {
  const e = parseReais(value.engineer);
  const t = parseReais(value.team);
  const valid = !Number.isNaN(e) && !Number.isNaN(t) && (e !== null || t !== null);
  const pricing: Pricing = { engineerMonthly: e ?? 0, teamMonthly: t ?? 0 };
  return (
    <div className="pricing">
      <div className="two">
        <label className="field">
          <span>Engenheiro (por mês)</span>
          <input
            className="input num"
            inputMode="decimal"
            placeholder="R$ 0"
            value={value.engineer}
            aria-invalid={Number.isNaN(e)}
            onChange={(ev) => onChange({ ...value, engineer: ev.target.value })}
          />
        </label>
        <label className="field">
          <span>Time parcial (por mês)</span>
          <input
            className="input num"
            inputMode="decimal"
            placeholder="R$ 0"
            value={value.team}
            aria-invalid={Number.isNaN(t)}
            onChange={(ev) => onChange({ ...value, team: ev.target.value })}
          />
        </label>
      </div>
      <p className="pricing-sum" aria-live="polite">
        {valid ? (
          <>
            Investimento mensal: <strong>{brl(quote(pricing, { engineers: 1 }).monthly)}</strong> com 1 engenheiro ·{' '}
            <strong>{brl(quote(pricing, { engineers: 2 }).monthly)}</strong> com 2
          </>
        ) : (
          'Sem valores, a proposta mostra só o esforço. O time parcial cobre PO, arquiteto, designer, DevOps e QA.'
        )}
      </p>
    </div>
  );
};
